import {
   ActionIcon,
   Alert,
   Badge,
   Button,
   Card,
   Center,
   Code,
   Container,
   CopyButton,
   Divider,
   Group,
   Image,
   List,
   Modal,
   Paper,
   PinInput,
   Stack,
   Text,
   ThemeIcon,
   Title,
   Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
   IconAlertCircle,
   IconCheck,
   IconCircleCheck,
   IconCopy,
   IconInfoCircle,
   IconKey,
   IconShieldLock,
   IconX,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToastNotification } from "../../hooks/useToastNotification";
import { HttpCodes } from "../../lib/data";
import { Endpoints } from "../../lib/endpoints";
import { PerformRequest } from "../../lib/usePerformRequest";
import { NonPaginatedResponse } from "../../types";

interface TwoFactorStatus {
   enabled: boolean;
}

interface TwoFactorSecret {
   secret: string;
   qrCodeUrl: string;
   manualEntryKey: string;
}

export default function Account() {
   const { user, reloadUser } = useAuth();
   const { success, error: showError } = useToastNotification();
   const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorStatus | null>(null);
   const [isLoadingStatus, setIsLoadingStatus] = useState(true);
   const [isGeneratingSecret, setIsGeneratingSecret] = useState(false);
   const [isEnabling, setIsEnabling] = useState(false);
   const [isDisabling, setIsDisabling] = useState(false);
   const [secretData, setSecretData] = useState<TwoFactorSecret | null>(null);
   const [showSetupModal, setShowSetupModal] = useState(false);
   const [showDisableModal, setShowDisableModal] = useState(false);

   const verificationForm = useForm({
      initialValues: {
         code: "",
      },
      validate: {
         code: (value) => {
            if (!value) return "Verification code is required";
            if (value.length !== 6) return "Code must be 6 digits";
            return null;
         },
      },
   });

   // Fetch 2FA status on mount
   useEffect(() => {
      fetchTwoFactorStatus();
   }, []);

   const fetchTwoFactorStatus = async () => {
      setIsLoadingStatus(true);
      try {
         const response = await PerformRequest<NonPaginatedResponse<TwoFactorStatus>>({
            method: Endpoints.GetTwoFactorStatus().method,
            route: Endpoints.GetTwoFactorStatus().route,
         });

         if (response && response.status === HttpCodes.Success) {
            setTwoFactorStatus(response.data?.data || { enabled: false });
         }
      } catch (error: any) {
         console.error("Failed to fetch 2FA status:", error);
      } finally {
         setIsLoadingStatus(false);
      }
   };

   const handleGenerateSecret = async () => {
      setIsGeneratingSecret(true);
      try {
         const response = await PerformRequest<NonPaginatedResponse<TwoFactorSecret>>({
            method: Endpoints.GetTwoFactorGenerateSecret().method,
            route: Endpoints.GetTwoFactorGenerateSecret().route,
         });

         if (response && response.status === HttpCodes.Success) {
            setSecretData(response.data?.data || null);
            setShowSetupModal(true);
            verificationForm.reset();
         } else {
            showError(response?.message || "Failed to generate 2FA secret");
         }
      } catch (error: any) {
         showError(error?.message || "Failed to generate 2FA secret");
      } finally {
         setIsGeneratingSecret(false);
      }
   };

   const handleEnableTwoFactor = async () => {
      const { hasErrors } = verificationForm.validate();
      if (hasErrors) return;

      setIsEnabling(true);
      try {
         const { code } = verificationForm.values;
         const response = await PerformRequest<NonPaginatedResponse<{ enabled: boolean }>>({
            method: Endpoints.GetTwoFactorEnable().method,
            route: Endpoints.GetTwoFactorEnable().route,
            data: { token: code },
         });

         if (response && response.status === HttpCodes.Success) {
            success("2FA enabled successfully!");
            setShowSetupModal(false);
            setSecretData(null);
            verificationForm.reset();
            await fetchTwoFactorStatus();
            await reloadUser();
         } else {
            showError(response?.message || "Invalid verification code");
         }
      } catch (error: any) {
         showError(error?.message || "Failed to enable 2FA");
      } finally {
         setIsEnabling(false);
      }
   };

   const handleDisableTwoFactor = async () => {
      setIsDisabling(true);
      try {
         const response = await PerformRequest<NonPaginatedResponse<any>>({
            method: Endpoints.GetTwoFactorDisable().method,
            route: Endpoints.GetTwoFactorDisable().route,
         });

         if (response && response.status === HttpCodes.Success) {
            success("2FA disabled successfully");
            setShowDisableModal(false);
            await fetchTwoFactorStatus();
            await reloadUser();
         } else {
            showError(response?.message || "Failed to disable 2FA");
         }
      } catch (error: any) {
         showError(error?.message || "Failed to disable 2FA");
      } finally {
         setIsDisabling(false);
      }
   };

   return (
      <Container size="xl" py="xl">
         <Stack gap="xl">
            {/* Header */}
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
               <Title
                  order={1}
                  size="3rem"
                  fw={800}
                  style={{
                     color: "#679369",
                     marginBottom: "0.5rem",
                  }}
               >
                  Account Security
               </Title>
               <Text size="lg" c="dimmed">
                  Manage your account security settings and two-factor authentication
               </Text>
            </motion.div>

            {/* Two-Factor Authentication Section */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
               <Paper
                  shadow="md"
                  p="xl"
                  radius="lg"
                  style={{
                     background: "white",
                     border: "2px solid #e5e7eb",
                  }}
               >
                  <Stack gap="xl">
                     {/* Section Header */}
                     <Group justify="space-between" align="flex-start">
                        <Group gap="md">
                           <ThemeIcon
                              size={48}
                              radius="md"
                              style={{
                                 background: "#E8F5E9",
                              }}
                           >
                              <IconShieldLock
                                 size={28}
                                 style={{
                                    color: "#699269",
                                 }}
                              />
                           </ThemeIcon>
                           <div>
                              <Title
                                 order={3}
                                 style={{
                                    color: "#111827",
                                    marginBottom: "0.25rem",
                                 }}
                              >
                                 Two-Factor Authentication (2FA)
                              </Title>
                              <Text size="sm" c="dimmed">
                                 Add an extra layer of security to your admin account
                              </Text>
                           </div>
                        </Group>
                        {!isLoadingStatus && (
                           <Badge
                              size="lg"
                              color={twoFactorStatus?.enabled ? "green" : "gray"}
                              leftSection={twoFactorStatus?.enabled ? <IconCheck size={16} /> : <IconX size={16} />}
                           >
                              {twoFactorStatus?.enabled ? "Enabled" : "Disabled"}
                           </Badge>
                        )}
                     </Group>

                     <Divider />

                     {/* How 2FA Works */}
                     <Card
                        p="lg"
                        style={{
                           background: "#F9FAFB",
                           border: "1px solid #E5E7EB",
                        }}
                     >
                        <Group gap="sm" mb="md">
                           <IconInfoCircle size={20} style={{ color: "#699269" }} />
                           <Text fw={600} size="lg">
                              How Two-Factor Authentication Works
                           </Text>
                        </Group>
                        <Text size="sm" c="dimmed" mb="md">
                           Two-factor authentication adds an extra layer of security to your admin account. After
                           entering your password, you'll need to provide a 6-digit code from your authenticator app.
                        </Text>
                        <List
                           size="sm"
                           spacing="xs"
                           icon={
                              <IconCircleCheck
                                 size={16}
                                 style={{
                                    color: "#699269",
                                 }}
                              />
                           }
                        >
                           <List.Item>
                              <strong>Step 1:</strong> Download an authenticator app (Google Authenticator, Microsoft
                              Authenticator, Authy, etc.)
                           </List.Item>
                           <List.Item>
                              <strong>Step 2:</strong> Scan the QR code or enter the secret key manually
                           </List.Item>
                           <List.Item>
                              <strong>Step 3:</strong> Enter the 6-digit code from your app to verify and enable 2FA
                           </List.Item>
                           <List.Item>
                              <strong>Step 4:</strong> Every time you log in, you'll need your password + the code from
                              your authenticator app
                           </List.Item>
                        </List>
                     </Card>

                     {/* 2FA Status and Actions */}
                     {isLoadingStatus ? (
                        <Center py="xl">
                           <Text c="dimmed">Loading 2FA status...</Text>
                        </Center>
                     ) : twoFactorStatus?.enabled ? (
                        <Stack gap="md">
                           <Alert
                              icon={<IconCircleCheck size={20} />}
                              title="2FA is Enabled"
                              color="green"
                              style={{
                                 background: "#F0FDF4",
                                 border: "1px solid #86EFAC",
                              }}
                           >
                              Your admin account is protected with two-factor authentication. You'll need to enter a
                              code from your authenticator app every time you log in.
                           </Alert>
                           <Button
                              color="red"
                              variant="outline"
                              onClick={() => setShowDisableModal(true)}
                              leftSection={<IconX size={18} />}
                           >
                              Disable 2FA
                           </Button>
                        </Stack>
                     ) : (
                        <Stack gap="md">
                           <Alert
                              icon={<IconAlertCircle size={20} />}
                              title="2FA is Disabled"
                              color="yellow"
                              style={{
                                 background: "#FFFBEB",
                                 border: "1px solid #FCD34D",
                              }}
                           >
                              Your admin account is not protected with two-factor authentication. Enable it now to add
                              an extra layer of security.
                           </Alert>
                           <Button
                              onClick={handleGenerateSecret}
                              loading={isGeneratingSecret}
                              leftSection={<IconShieldLock size={18} />}
                              style={{
                                 background: "linear-gradient(135deg, #699269 0%, #558B2F 100%)",
                                 border: "none",
                              }}
                              size="md"
                           >
                              Enable Two-Factor Authentication
                           </Button>
                        </Stack>
                     )}
                  </Stack>
               </Paper>
            </motion.div>
         </Stack>

         {/* Setup Modal */}
         <Modal
            opened={showSetupModal}
            onClose={() => {
               if (!isEnabling) {
                  setShowSetupModal(false);
                  setSecretData(null);
                  verificationForm.reset();
               }
            }}
            title="Set Up Two-Factor Authentication"
            size="lg"
            centered
         >
            <Stack gap="xl">
               <Alert icon={<IconInfoCircle size={20} />} title="Scan QR Code" color="blue">
                  Open your authenticator app (Google Authenticator, Microsoft Authenticator, etc.) and scan this QR
                  code.
               </Alert>

               {secretData && (
                  <>
                     {/* QR Code */}
                     <Center>
                        <Paper
                           p="md"
                           style={{
                              background: "white",
                              border: "2px solid #E5E7EB",
                              borderRadius: "8px",
                           }}
                        >
                           <Image src={secretData.qrCodeUrl} alt="2FA QR Code" width={200} height={200} />
                        </Paper>
                     </Center>

                     {/* Manual Entry */}
                     <Card
                        p="md"
                        style={{
                           background: "#F9FAFB",
                           border: "1px solid #E5E7EB",
                        }}
                     >
                        <Group gap="xs" mb="sm">
                           <IconKey size={18} style={{ color: "#699269" }} />
                           <Text fw={600} size="sm">
                              Can't scan? Enter this code manually:
                           </Text>
                        </Group>
                        <Group gap="xs">
                           <Code
                              style={{
                                 flex: 1,
                                 fontSize: "14px",
                                 padding: "8px",
                              }}
                           >
                              {secretData.manualEntryKey}
                           </Code>
                           <CopyButton value={secretData.manualEntryKey}>
                              {({ copied, copy }) => (
                                 <Tooltip label={copied ? "Copied!" : "Copy"} withArrow>
                                    <ActionIcon color={copied ? "green" : "gray"} onClick={copy}>
                                       {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                                    </ActionIcon>
                                 </Tooltip>
                              )}
                           </CopyButton>
                        </Group>
                     </Card>

                     {/* Verification Code Input */}
                     <Stack gap="md">
                        <Text fw={600} size="sm">
                           Enter the 6-digit code from your authenticator app to verify and enable 2FA:
                        </Text>
                        <form
                           onSubmit={(e) => {
                              e.preventDefault();
                              handleEnableTwoFactor();
                           }}
                        >
                           <Stack gap="md">
                              <Center>
                                 <PinInput
                                    length={6}
                                    size="md"
                                    {...verificationForm.getInputProps("code")}
                                    type="number"
                                    oneTimeCode
                                    autoFocus
                                 />
                              </Center>
                              {verificationForm.errors.code && (
                                 <Text size="sm" c="red" ta="center">
                                    {verificationForm.errors.code}
                                 </Text>
                              )}
                              <Group justify="flex-end" mt="md">
                                 <Button
                                    variant="subtle"
                                    onClick={() => {
                                       setShowSetupModal(false);
                                       setSecretData(null);
                                       verificationForm.reset();
                                    }}
                                    disabled={isEnabling}
                                 >
                                    Cancel
                                 </Button>
                                 <Button
                                    type="submit"
                                    onClick={handleEnableTwoFactor}
                                    loading={isEnabling}
                                    style={{
                                       background: "linear-gradient(135deg, #699269 0%, #558B2F 100%)",
                                    }}
                                 >
                                    Verify & Enable
                                 </Button>
                              </Group>
                           </Stack>
                        </form>
                     </Stack>
                  </>
               )}
            </Stack>
         </Modal>

         {/* Disable Confirmation Modal */}
         <Modal
            opened={showDisableModal}
            onClose={() => {
               if (!isDisabling) {
                  setShowDisableModal(false);
               }
            }}
            title="Disable Two-Factor Authentication"
            centered
         >
            <Stack gap="md">
               <Alert icon={<IconAlertCircle size={20} />} title="Are you sure?" color="red">
                  Disabling 2FA will remove the extra security layer from your admin account. You'll only need your
                  password to log in.
               </Alert>
               <Group justify="flex-end" mt="md">
                  <Button variant="subtle" onClick={() => setShowDisableModal(false)} disabled={isDisabling}>
                     Cancel
                  </Button>
                  <Button color="red" onClick={handleDisableTwoFactor} loading={isDisabling}>
                     Disable 2FA
                  </Button>
               </Group>
            </Stack>
         </Modal>
      </Container>
   );
}
